import type { UpdateMbcBriefBody } from "@nlc/shared-types";

import { useMemo, useState } from "react";

import { Button } from "@/components/button";
import { Sheet, SheetContent } from "@/components/sheet";
import { toast } from "@/components/toast/toast-queue";
import { BriefText } from "@/features/request/mbc-review/components/BriefText";
import { BriefTextEditor } from "@/features/request/mbc-review/components/BriefTextEditor";
import { EditButton } from "@/features/request/mbc-review/components/EditButton";
import {
  type Deliverables,
  EditSizeModal,
} from "@/features/request/mbc-review/components/EditSizeModal";
import { mapBriefToReview } from "@/features/request/mbc-review/map-brief";

import type { BriefMemory } from "../hooks/useBriefMemory";

import { patchBrief } from "../lib/brief-api";
import { requestIdFromSessionId } from "../lib/session-ids";
import { AssetUploadBlock } from "./upload-assets/AssetUploadBlock";
import { type AssetGroupChange, readGroup } from "./upload-assets/types";

type AlphaBriefSheetProps = {
  canSubmit: boolean;
  isOpen: boolean;
  isSubmitting?: boolean;
  memory: BriefMemory | null;
  onBriefSaved: () => void;
  onOpenChange: (isOpen: boolean) => void;
  onSubmit: () => void;
  resourceId?: string;
  submitError?: null | string;
  threadId?: string;
};

type TextField = "textForDesign" | "title" | "visualDirection";

const noop = () => {};

function Section({
  children,
  onEdit,
  title,
}: {
  children: React.ReactNode;
  onEdit?: () => void;
  title: string;
}) {
  return (
    <section className="flex flex-col gap-sm">
      <div className="flex items-center justify-between">
        <h3 className="text-md font-semibold text-text-primary">{title}</h3>
        {onEdit ? (
          <EditButton label={title.toLowerCase()} onPress={onEdit} />
        ) : null}
      </div>
      {children}
    </section>
  );
}

export function AlphaBriefSheet({
  canSubmit,
  isOpen,
  isSubmitting = false,
  memory,
  onBriefSaved,
  onOpenChange,
  onSubmit,
  resourceId,
  submitError,
  threadId,
}: AlphaBriefSheetProps) {
  const { brief } = useMemo(() => mapBriefToReview(memory), [memory]);
  const [editingField, setEditingField] = useState<null | TextField>(null);
  const [isEditingDeliverables, setIsEditingDeliverables] = useState(false);
  const requestId = threadId ? requestIdFromSessionId(threadId) : null;
  const canEdit = Boolean(threadId);
  const deliverables = [
    ...brief.fileSizes.map((item) => `${item.label} · ${item.size}`),
    ...(brief.fileTypes.length > 0 ? [brief.fileTypes.join(", ")] : []),
  ].join("\n");

  const save = (patch: UpdateMbcBriefBody) => {
    if (!threadId) return;

    patchBrief(threadId, resourceId, patch)
      .then(onBriefSaved)
      .catch(() => {
        toast.add({
          description: "Please try again.",
          title: "Couldn't save your brief",
          type: "error",
        });
      });
  };

  const saveDeliverables = ({ fileSizes, fileTypes }: Deliverables) =>
    save({
      fileSizes: fileSizes.map((item) => ({
        dimensions: item.size,
        label: item.label,
      })),
      fileTypes,
    });

  const handleAssetChange =
    (target: "assets" | "inspirations") => (change: AssetGroupChange) =>
      save({ assetChanges: [{ target, ...change }] });

  const textSection = (
    field: TextField,
    title: string,
    placeholder: string,
    value: string,
  ) => (
    <Section
      onEdit={canEdit ? () => setEditingField(field) : undefined}
      title={title}
    >
      {editingField === field ? (
        <BriefTextEditor
          onCancel={() => setEditingField(null)}
          onSave={(next) => save({ [field]: next })}
          placeholder={placeholder}
          value={value}
        />
      ) : (
        <BriefText placeholder={placeholder} value={value} />
      )}
    </Section>
  );

  return (
    <Sheet
      classNames={{ content: "w-[min(480px,92vw)]" }}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      side="right"
    >
      <SheetContent
        aria-label="Brief"
        className="flex h-full flex-col bg-bg-primary outline-hidden"
      >
        <div className="flex flex-1 flex-col gap-3xl overflow-y-auto px-3xl py-4xl">
          {editingField === "title" ? (
            <BriefTextEditor
              onCancel={() => setEditingField(null)}
              onSave={(next) => save({ title: next })}
              placeholder="Untitled brief"
              value={brief.title}
            />
          ) : (
            <div className="flex items-start justify-between gap-md">
              <h2 className="text-display-xs font-semibold text-text-primary">
                {brief.title || "Untitled brief"}
              </h2>
              {canEdit ? (
                <EditButton
                  label="title"
                  onPress={() => setEditingField("title")}
                />
              ) : null}
            </div>
          )}
          {textSection(
            "visualDirection",
            "Visual direction",
            "No visual direction yet",
            brief.visualDirection,
          )}
          {textSection(
            "textForDesign",
            "Text for design",
            "No text for this design yet",
            brief.textForDesign,
          )}
          <Section
            onEdit={canEdit ? () => setIsEditingDeliverables(true) : undefined}
            title="Deliverables"
          >
            <BriefText placeholder="Not set yet" value={deliverables} />
          </Section>
          {(["assets", "inspirations"] as const).map((target) => {
            const title = target === "assets" ? "Assets" : "Inspirations";

            return (
              <Section key={target} title={title}>
                <AssetUploadBlock
                  onChange={handleAssetChange(target)}
                  onFilesChanged={() => save({})}
                  onUploadingChange={noop}
                  requestId={requestId}
                  target={target}
                  title={title}
                  value={readGroup(memory?.[target])}
                />
              </Section>
            );
          })}
        </div>
        <div className="flex flex-col gap-sm border-t border-border-secondary bg-bg-primary px-3xl py-lg">
          <Button
            isDisabled={!canSubmit}
            isLoading={isSubmitting}
            onPress={onSubmit}
            variant="primary"
          >
            Submit brief
          </Button>
          {submitError ? (
            <p
              className="text-center text-sm text-text-error-primary"
              role="alert"
            >
              {submitError}
            </p>
          ) : null}
          {canSubmit ? null : (
            <p className="text-center text-sm text-text-tertiary">
              Keep chatting until your brief is ready to submit.
            </p>
          )}
        </div>
        <EditSizeModal
          isOpen={isEditingDeliverables}
          onOpenChange={setIsEditingDeliverables}
          onSave={saveDeliverables}
          value={{ fileSizes: brief.fileSizes, fileTypes: brief.fileTypes }}
        />
      </SheetContent>
    </Sheet>
  );
}
